<template>
  <Transition name="fade">
    <div
      v-if="showModal"
      class="modal fade show"
      style="display: block; padding-right: 15px"
    >
      <div class="modal-backdrop fade show" @click="handleBackdropClick"></div>
      <div :class="modalDialogClasses">
        <div class="modal-content">
          <!--
            Default header slot. Consumers that want full control over the
            top of the modal (custom icon + title + description rows, no
            separator, etc.) can set `:noHeader="true"` — the close (×)
            button is then rendered as a floating top-right control so the
            body can own the full chrome.
          -->
          <div v-if="!noHeader" class="modal-header">
            <h3 class="modal-title" id="verticallyCenteredModalLabel">
              <slot name="title"></slot>
            </h3>
            <button
              v-if="dismissible"
              class="modal-close-btn"
              type="button"
              @click="handleModalClose"
              :aria-label="$t('common.close')"
            >
              <font-awesome-icon icon="times" />
            </button>
          </div>
          <button
            v-if="noHeader && dismissible"
            class="modal-close-btn modal-close-btn--floating"
            type="button"
            @click="handleModalClose"
            :aria-label="$t('common.close')"
          >
            <font-awesome-icon icon="times" />
          </button>
          <div class="modal-body">
            <slot name="body"></slot>
          </div>
          <div v-if="!noFooter" class="modal-footer">
            <slot name="footer">
              <button
                v-if="!hideSubmitButton"
                class="btn btn-primary"
                type="button"
                :disabled="submitDisabled"
                @click="
                  submit
                    ? submit()
                    : dismissible
                      ? handleModalClose()
                      : undefined
                "
              >
                {{ submitButtonText }}
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { pushEscapeHandler } from "@/components/common/modalEscapeStack";
import { computed, onUnmounted, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    submitButtonText: string;
    submit?: (() => void) | null;
    submitDisabled?: boolean;
    hideSubmitButton?: boolean;
    dismissible?: boolean;
    size?: "" | "sm" | "lg" | "xl";
    /**
     * Skip the default modal-header block entirely. The close (×) button
     * is then rendered as a floating top-right control and the body slot
     * becomes the sole owner of top chrome. Useful when the design wants
     * a custom icon/title/description layout without the header's padding
     * and separator.
     */
    noHeader?: boolean;
    /**
     * Skip the default modal-footer block (including the default submit
     * button). Consumers can render their own footer inside the body slot
     * if they need one. Equivalent to leaving the footer slot empty but
     * also removes the wrapper's padding and background.
     */
    noFooter?: boolean;
    /**
     * Asked whenever `handleModalClose` runs (the ×, the backdrop, Escape, the default footer button,
     * or a parent through the ref); returning false keeps it open, e.g. while it holds unsaved edits.
     * Closing through `showModal` or `toggleModal` is not asked.
     */
    beforeClose?: (() => boolean) | null;
  }>(),
  {
    submit: null,
    submitDisabled: false,
    hideSubmitButton: false,
    dismissible: true,
    size: "",
    noHeader: false,
    noFooter: false,
    beforeClose: null,
  }
);

const emit = defineEmits<{
  close: [];
  setForceToggle: [forceToggleModal: (value: boolean) => void];
}>();

const showModal = ref(false);

const modalDialogClasses = computed(() => {
  const baseClasses = "modal-dialog modal-dialog-centered";
  if (props.size) {
    return `${baseClasses} modal-${props.size}`;
  }
  return baseClasses;
});

const toggleModal = () => {
  showModal.value = !showModal.value;
};

const handleModalClose = () => {
  if (props.beforeClose && !props.beforeClose()) return;
  toggleModal();
  emit("close");
};

const handleBackdropClick = () => {
  if (!props.dismissible) {
    return;
  }
  handleModalClose();
};

// A non-dismissible modal still takes the Escape, so it never reaches the modal beneath it.
const handleEscKey = () => {
  if (props.dismissible) {
    handleModalClose();
  }
};

let releaseEscape: (() => void) | null = null;

watch(showModal, (isOpen) => {
  releaseEscape?.();
  releaseEscape = isOpen ? pushEscapeHandler(handleEscKey) : null;
});

onUnmounted(() => {
  releaseEscape?.();
});

const forceToggleModal = (value: boolean) => {
  showModal.value = value;
};

// Expose the method
emit("setForceToggle", forceToggleModal);

// `<script setup>` instances are closed by default: expose what parents drive
// through a template ref (`modalRef.value.toggleModal()`, `modalRef.value.showModal = true`).
defineExpose({
  showModal,
  toggleModal,
  handleModalClose,
});
</script>

<style scoped lang="scss">
// Replace Phoenix's solid-black 50% backdrop with a blurred tint so the
// content behind the modal reads as soft context, not a stark cutout.
// Phoenix does `background: #000; opacity: 0.5` which makes backdrop-filter
// inert — we need the opacity baked into the color for the filter to bite.
.modal-backdrop {
  --phoenix-backdrop-bg: transparent;
  background: rgba(34, 40, 52, 0.45);
  backdrop-filter: blur(3px);

  &.show {
    --phoenix-backdrop-opacity: 1;
    opacity: 1;
  }
}

.modal-content {
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  .modal-header {
    border: 0;
    .modal-title {
      font-size: 1.5rem;
    }
  }
  .modal-body {
    padding-bottom: 0;
    padding-top: 0;
  }
  .accordion-item {
    border: 0;
  }
  .modal-footer {
    border: 0;
  }
}

// Shared close (×) button: same look + hover for the in-header and the
// floating (noHeader) variants.
.modal-close-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  display: grid;
  place-items: center;
  border: 0;
  background: transparent;
  border-radius: 8px;
  color: var(--phoenix-gray-500);
  cursor: pointer;
  transition:
    background 0.12s ease,
    color 0.12s ease;

  &:hover {
    background: var(--phoenix-gray-100);
    color: var(--phoenix-gray-900);
  }
}

// Phoenix only right-aligns `.btn-close`; ours isn't one, so pin it right.
.modal-header .modal-close-btn {
  margin-left: auto;
}

// Floating variant (noHeader): overlay the top-right of the modal-content so
// the body slot can claim the full chrome area.
.modal-close-btn--floating {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
}

.modal {
  display: block;
}

.modal-dialog {
  z-index: var(--phoenix-modal-zindex);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease-in;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
